import { classifyPaymentTerms } from "@shared/payment-terms";
import { dueDateForTerms } from "./supplierPaymentTerms";

export type ReceiptCashChoice = {
  cashBoxId?: string;
  payerBranchId?: string;
  forBranchId?: string;
  defaultResolved?: boolean;
  agreementReady?: boolean;
  payFromCashBox?: boolean;
  cashPaymentAmount?: string;
  amountEdited?: boolean;
  choiceEdited?: boolean;
  cashTakingId?: string;
  cashExtraPaymentChoice?: "box" | "owed";
  balanceReady?: boolean;
  cashBoxBalance?: string;
  canPay?: boolean;
  heldAmount?: string;
  takingReady?: boolean;
};

export function cashDefault(terms: string, variesDefault: boolean | undefined) {
  const method = classifyPaymentTerms(terms);
  return method === "cash" ? true : method === "credit" ? false : variesDefault;
}

export function cashCents(value: string | number): number | null {
  const match = String(value).match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] || "").padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents : null;
}

export function cashChoiceReady(choice: ReceiptCashChoice, total: string | number) {
  if (choice.agreementReady === false) return false;
  if (typeof choice.payFromCashBox !== "boolean") return false;
  if (!choice.payFromCashBox) return true;
  const amount = cashCents(choice.cashPaymentAmount || "");
  const max = cashCents(total);
  if (amount === null || max === null || amount < 0 || amount > max) return false;
  // Zero is credit, not a cash write. It requires no box read or cash permission.
  if (amount === 0) return true;
  if (!choice.defaultResolved || !choice.balanceReady) return false;
  if (choice.cashTakingId && !choice.takingReady) return false;
  const debit = receiptBoxDebit(choice);
  const balance = cashCents(choice.cashBoxBalance || "");
  return debit !== null && balance !== null && debit <= balance && (debit === 0 || choice.canPay === true);
}

/** Held cash has already left the box. Only new extra cash is balance-capped. */
export function receiptBoxDebit(choice: ReceiptCashChoice): number | null {
  const amount = cashCents(choice.cashPaymentAmount || "");
  if (amount === null) return null;
  if (!choice.cashTakingId) return amount;
  const held = cashCents(choice.heldAmount || "");
  if (held === null) return null;
  return choice.cashExtraPaymentChoice === "owed" ? 0 : Math.max(0, amount - held);
}

export function receiptCashDefaultAmount(total: string | number, balance: string | number) {
  return (Math.min(cashCents(total) ?? 0, cashCents(balance) ?? 0) / 100).toFixed(2);
}

export function receiptHasCashPayment(choice: ReceiptCashChoice) {
  return choice.payFromCashBox === true && (cashCents(choice.cashPaymentAmount || "") ?? 0) > 0;
}

export function receiptDueDateForTerms(date: string, terms: string) {
  return terms === "cash_on_delivery" || terms === "end_of_month" || /^net_\d+$/i.test(terms)
    ? dueDateForTerms(date, terms) : "";
}

export function receiptAgreementReady(full: boolean, terms: string, dueDate?: string) {
  if (full) return true;
  if (!classifyPaymentTerms(terms)) return false;
  const explicitDate = !!dueDate && /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && Number.isFinite(Date.parse(dueDate));
  return explicitDate || terms === "cash_on_delivery" || terms === "end_of_month" || /^net_\d+$/i.test(terms);
}

export function receiptCashPayload(choice: ReceiptCashChoice, total: string | number, terms: string, date: string, dueDate?: string) {
  if (!cashChoiceReady(choice, total)) throw new Error("Review the cash box choice and payment amount before confirming.");
  const paid = choice.payFromCashBox ? cashCents(choice.cashPaymentAmount!)! : 0;
  const full = choice.payFromCashBox && paid > 0 && paid === cashCents(total);
  if (!receiptAgreementReady(!!full, terms, dueDate)) throw new Error("Choose valid agreed payment terms and a determinable or explicit due date before confirming.");
  return {
    payFromCashBox: choice.payFromCashBox!,
    cashPaymentAmount: (paid / 100).toFixed(2),
    ...(paid > 0 && choice.cashBoxId ? { cashBoxId: choice.cashBoxId } : {}),
    paymentMethod: full ? "cash" as const : "credit" as const,
    paymentTerms: full ? "cash_on_delivery" : terms,
    dueDate: full ? date : dueDate || receiptDueDateForTerms(date, terms),
    ...(paid > 0 && choice.cashTakingId ? { cashTakingId: choice.cashTakingId, cashExtraPaymentChoice: choice.cashExtraPaymentChoice } : {}),
  };
}